import { InputHTMLAttributes, LabelHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

const Wrapper = styled.label<
    LabelHTMLAttributes<HTMLLabelElement> & { disabled?: boolean }
>`
    line-height: 1.6rem;
    display: inline-flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    ${({ disabled }) =>
        disabled &&
        css`
            color: ${tokens.text.disabled};
        `}

    &:focus-within {
        border-color: ${tokens.input.borderFocus};
    }
`;

export type RadioButtonProps = Readonly<
    InputHTMLAttributes<HTMLInputElement> & {
        name: string;
        label?: ReactNode;
        className?: string;
        labelClassName?: string;
    }
>;

export const RadioButtonInput = styled.input<{ disabled?: boolean }>`
    width: 15px;
    height: 15px;
    appearance: none;
    outline: none;
    border-radius: 999px;
    border: 2px solid ${tokens.input.border};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    &:before {
        content: '';
        width: 5px;
        height: 5px;
        border-radius: 999px;
        box-sizing: border-box;
        background: inherit;
    }

    &:disabled {
        cursor: not-allowed;
    }

    &:hover,
    &:focus {
        border-color: ${tokens.action.disabledBackground};
    }

    &:focus-visible {
        outline: 2px solid ${tokens.action.disabledBackground};
    }

    &:checked {
        border-color: ${tokens.input.borderFocus};

        &:before {
            background: ${tokens.input.borderFocus};
        }
    }
`;

export function RadioButton({
    name,
    label,
    labelClassName,
    className,
    disabled,
    checked,
    ...rest
}: RadioButtonProps) {
    return (
        <div>
            <Wrapper role="radio" aria-checked={checked} disabled={disabled}>
                <RadioButtonInput
                    {...rest}
                    type="radio"
                    name={name}
                    disabled={disabled}
                    checked={checked}
                    className={`mr-5 ${className ?? ''}`}
                />
                {label && <div className={labelClassName}>{label}</div>}
            </Wrapper>
        </div>
    );
}
